import { useMemo } from 'react';
import { PaymentsAbTestOrg } from '@wetags/payments-ab-test';
import dayjs from 'dayjs';
import { InvoiceModel } from '@frontend/api-invoices';
import { formatCentsToCurrency } from '@frontend/currency';
import { useTranslation } from '@frontend/i18n';
import { paymentsSentry } from '@frontend/payments-hooks';
import { ChipProps, formatDate, SkeletonLoader } from '@frontend/design-system';
import { InvoiceCardDetails as InvoiceCardDetailsV1 } from './invoice-card/v1/invoice-card-details';
import { InvoiceCardDetails as InvoiceCardDetailsV2 } from './invoice-card/v2/invoice-card-details';

const getFormattedDate = (date: string, newInvoiceCard: boolean) => {
  if (newInvoiceCard) {
    return formatDate(date, 'MMM D, YYYY', true);
  }

  const billedAt = dayjs(date);
  if (billedAt.year() === dayjs().year()) {
    return formatDate(date, 'MMMM D', true);
  }
  return formatDate(date, 'MMM D, YYYY', true);
};

export type InvoiceCardProps = React.PropsWithChildren<{
  invoice: InvoiceModel;
  className?: string;
}>;

export function InvoiceCard({ invoice, className, children }: InvoiceCardProps) {
  const { t } = useTranslation();
  const [newInvoiceCard, isLoading] = PaymentsAbTestOrg.useWetag('payments-panel-new-invoice-card', {});

  const formattedBilledAt = useMemo(() => {
    return getFormattedDate(invoice.billedAt, newInvoiceCard);
  }, [invoice, newInvoiceCard]);

  const formattedBilledAmount = useMemo(() => {
    return formatCentsToCurrency(invoice.billedAmount, false);
  }, [invoice]);

  const formattedOwedAmount = useMemo(() => {
    return formatCentsToCurrency(invoice.billedAmount - (invoice.payment?.paidAmount || 0), false);
  }, [invoice]);

  const billedAtData = useMemo(() => {
    try {
      // get count of days passed
      const daysPassed = dayjs().startOf('day').diff(dayjs(invoice.billedAt).startOf('day'), 'day');

      let variant: ChipProps['variant'] = 'neutral';
      let daysPassedLabel = '';
      if (daysPassed === 1) {
        daysPassedLabel = t(`1 Day`);
      } else if (daysPassed < 30) {
        daysPassedLabel = t(`{{ days }} Days`, { days: daysPassed });
      } else if (daysPassed < 60) {
        variant = 'warn';
        daysPassedLabel = t(`{{ days }}+ Days`, { days: 30 });
      } else if (daysPassed < 90) {
        variant = 'tangerine';
        daysPassedLabel = t(`{{ days }}+ Days`, { days: 60 });
      } else {
        variant = 'critical';
        daysPassedLabel = t(`{{ days }}+ Days`, { days: 90 });
      }

      return {
        variant,
        daysPassedLabel,
      };
    } catch (error) {
      paymentsSentry.error(
        'Failed to calculate days passed',
        'InvoiceCard',
        error instanceof Error ? error.message : 'Unknown error calculating days passed'
      );
      return {
        variant: 'neutral' as ChipProps['variant'],
        daysPassedLabel: '',
      };
    }
  }, [invoice, t]);

  const billedAtDataV2 = useMemo(() => {
    try {
      // get count of days passed
      const daysPassed = dayjs().startOf('day').diff(dayjs(invoice.billedAt).startOf('day'), 'day');

      let variant: ChipProps['variant'] = 'primary';
      let daysPassedLabel = t('Due today');

      if (daysPassed >= 1 && daysPassed < 30) {
        variant = 'warn';
        daysPassedLabel = daysPassed === 1 ? t('1 day overdue') : t('{{ count }} days overdue', { count: daysPassed });
      } else if (daysPassed >= 30) {
        variant = 'critical';
        daysPassedLabel = daysPassed === 30 ? t('30 days overdue') : t('30+ days overdue');
      }

      return {
        variant,
        daysPassedLabel,
      };
    } catch (error) {
      paymentsSentry.error(
        'Failed to calculate days passed for V2',
        'InvoiceCard',
        error instanceof Error ? error.message : 'Unknown error calculating days passed for V2'
      );
      return {
        variant: 'neutral' as ChipProps['variant'],
        daysPassedLabel: '',
      };
    }
  }, [invoice, t]);

  const InvoiceCardDetails = useMemo(
    () => (newInvoiceCard ? InvoiceCardDetailsV2 : InvoiceCardDetailsV1),
    [newInvoiceCard]
  );

  if (isLoading) {
    return <SkeletonLoader width='100%' height='200px' count={1} distance={20} animation='shimmer' />;
  }

  return (
    <InvoiceCardDetails
      className={className}
      invoice={invoice}
      formattedBilledAt={formattedBilledAt}
      billedAtData={newInvoiceCard ? billedAtDataV2 : billedAtData}
      formattedBilledAmount={formattedBilledAmount}
      formattedOwedAmount={formattedOwedAmount}
    >
      {children}
    </InvoiceCardDetails>
  );
}
